import { combineReducers, configureStore } from "@reduxjs/toolkit";
import {
  persistReducer,
  persistStore,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from "redux-persist";
import storage from "redux-persist/lib/storage";
import { AuthApi } from "./services/auth/auth.api";
import { UserApi } from "./services/user/user.api";
import { authSlice } from "./features/auth/auth.slice";
import { userSlice } from "./features/user/user.slice";
import { TableApi } from "./services/table/table.api";
import { MenuApi } from "./services/menu/menu.api";
import { RestaurantApi } from "./services/restaurant/restaurant.api";
import { OrderApi } from "./services/order/order.api";
import { NotificationApi } from "./services/notification/notification.api";
import { notificationSlice } from "./slices/notification.slice";
import { SearchApi } from "./services/search/search.api";
import { KpiApi } from "./services/kpi/kpi.api";
import { MediaApi } from "./services/media/media.api";
import { MenuComboApi } from "./services/menu/menu_combo.api";
import { dashboardApi } from "./services/dashboard/dashboard.api";
import { CustomerApi } from "./services/customer.api";
import { dailyDishesApi } from "./services/dailyDishes/dailyDishes.api";
import { PaymentApi } from "./services/pos/payment.api";
import { RegisterSessionApi } from "./services/pos/registerSession.api";
import { IngredientApi } from "./services/inventory/ingredient.api";
import { IngredientPackagingApi } from "./services/inventory/ingredientPackaging.api";
import { RecipeApi } from "./services/inventory/recipe.api";
import { StockAdjustmentApi } from "./services/inventory/stockAdjustment.api";
import { SupplierApi } from "./services/inventory/supplier.api";
import { PurchaseOrderApi } from "./services/inventory/purchaseOrder.api";
import { StockCountApi } from "./services/inventory/stockCount.api";
import { InventoryReportApi } from "./services/inventory/inventoryReport.api";
import { ReceiptApi } from "./services/pos/receipt.api";
import { SalesReportApi } from "./services/pos/salesReport.api";
import { StaffLogApi } from "./services/pos/staffLog.api";
import { MenuFormulaApi } from "./services/menu/menu_formula.api";
import { AccessRoleApi } from "./services/permission/accessRole.api";
import { WhatsAppApi } from "./services/integrations/whatsapp.api";
import { ExpenseApi } from "./services/expense/expense.api";
import { posSlice } from "./slices/pos.slice";
import { createApiResetMiddleware } from "./apiReset";

// Every createApi slice, so logout can drop all their caches (SFRESTO-164).
// Add new APIs here too.
const API_SLICES = [
  AuthApi,
  UserApi,
  TableApi,
  MenuApi,
  RestaurantApi,
  OrderApi,
  NotificationApi,
  SearchApi,
  KpiApi,
  MediaApi,
  MenuComboApi,
  dashboardApi,
  CustomerApi,
  dailyDishesApi,
  PaymentApi,
  RegisterSessionApi,
  IngredientApi,
  IngredientPackagingApi,
  RecipeApi,
  StockAdjustmentApi,
  SupplierApi,
  PurchaseOrderApi,
  StockCountApi,
  InventoryReportApi,
  ReceiptApi,
  SalesReportApi,
  StaffLogApi,
  MenuFormulaApi,
  AccessRoleApi,
  WhatsAppApi,
  ExpenseApi,
];

export const rootReducers = combineReducers({
  [authSlice.name]: authSlice.reducer,
  [userSlice.name]: userSlice.reducer,
  [notificationSlice.name]: notificationSlice.reducer,
  [posSlice.name]: posSlice.reducer,

  // Api
  [AuthApi.reducerPath]: AuthApi.reducer,
  [UserApi.reducerPath]: UserApi.reducer,
  [TableApi.reducerPath]: TableApi.reducer,
  [MenuApi.reducerPath]: MenuApi.reducer,
  [RestaurantApi.reducerPath]: RestaurantApi.reducer,
  [OrderApi.reducerPath]: OrderApi.reducer,
  [NotificationApi.reducerPath]: NotificationApi.reducer,
  [SearchApi.reducerPath]: SearchApi.reducer,
  [KpiApi.reducerPath]: KpiApi.reducer,
  [MediaApi.reducerPath]: MediaApi.reducer,
  [MenuComboApi.reducerPath]: MenuComboApi.reducer,
  [dashboardApi.reducerPath]: dashboardApi.reducer,
  [CustomerApi.reducerPath]: CustomerApi.reducer,
  [dailyDishesApi.reducerPath]: dailyDishesApi.reducer,
  [PaymentApi.reducerPath]: PaymentApi.reducer,
  [RegisterSessionApi.reducerPath]: RegisterSessionApi.reducer,
  [IngredientApi.reducerPath]: IngredientApi.reducer,
  [IngredientPackagingApi.reducerPath]: IngredientPackagingApi.reducer,
  [RecipeApi.reducerPath]: RecipeApi.reducer,
  [StockAdjustmentApi.reducerPath]: StockAdjustmentApi.reducer,
  [SupplierApi.reducerPath]: SupplierApi.reducer,
  [PurchaseOrderApi.reducerPath]: PurchaseOrderApi.reducer,
  [StockCountApi.reducerPath]: StockCountApi.reducer,
  [InventoryReportApi.reducerPath]: InventoryReportApi.reducer,
  [ReceiptApi.reducerPath]: ReceiptApi.reducer,
  [SalesReportApi.reducerPath]: SalesReportApi.reducer,
  [StaffLogApi.reducerPath]: StaffLogApi.reducer,
  [MenuFormulaApi.reducerPath]: MenuFormulaApi.reducer,
  [AccessRoleApi.reducerPath]: AccessRoleApi.reducer,
  [WhatsAppApi.reducerPath]: WhatsAppApi.reducer,
  [ExpenseApi.reducerPath]: ExpenseApi.reducer,
});

const persistConfig = {
  key: "root",
  storage,
  whitelist: ["user", "auth", "pos"],
};

const persistedReducer = persistReducer(persistConfig, rootReducers);

export const store = configureStore({
  reducer: persistedReducer,
  devTools: process.env.NODE_ENV !== "production",
  // @ts-ignore
  middleware: (getDefaultMiddleware) => [
    ...getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),
    AuthApi.middleware,
    UserApi.middleware,
    TableApi.middleware,
    MenuApi.middleware,
    RestaurantApi.middleware,
    OrderApi.middleware,
    NotificationApi.middleware,
    SearchApi.middleware,
    KpiApi.middleware,
    MediaApi.middleware,
    MenuComboApi.middleware,
    dashboardApi.middleware,
    CustomerApi.middleware,
    dailyDishesApi.middleware,
    PaymentApi.middleware,
    RegisterSessionApi.middleware,
    IngredientApi.middleware,
    IngredientPackagingApi.middleware,
    RecipeApi.middleware,
    StockAdjustmentApi.middleware,
    SupplierApi.middleware,
    PurchaseOrderApi.middleware,
    StockCountApi.middleware,
    InventoryReportApi.middleware,
    ReceiptApi.middleware,
    SalesReportApi.middleware,
    StaffLogApi.middleware,
    MenuFormulaApi.middleware,
    AccessRoleApi.middleware,
    WhatsAppApi.middleware,
    ExpenseApi.middleware,
    createApiResetMiddleware(API_SLICES),
  ],
});

export const persistor = persistStore(store);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
